import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import StudentFeedbackCard from '../components/StudentFeedbackCard';
import {
    Box, Grid, GridItem, VStack, HStack, Text, Select, Input, Button,
    Textarea, IconButton, Image, useToast, Badge, Card, CardBody,
    Divider, Avatar, FormControl, FormLabel, Icon, Skeleton, Heading,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, ModalFooter, Tooltip, useBreakpointValue,
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter,
    AlertDialogHeader, AlertDialogOverlay, Alert, AlertIcon, AlertDescription
} from '@chakra-ui/react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import {
    FiSave, FiUpload, FiPlus, FiTrash2, FiCheckCircle,
    FiSend, FiImage, FiFileText, FiRefreshCw,
    FiArrowLeft, FiChevronLeft, FiChevronRight, FiClock, FiX, FiMessageSquare
} from 'react-icons/fi';
import { authAPI, resolveImageUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';
// WEEKDAYS 直接沿用手機月曆元件匯出的那一份，而不是在這裡再宣告一次：
// 桌面與手機是同一頁的兩個分支，星期表頭一旦各寫各的就會偷偷長歪（本頁先前就是桌面
// 星期一開頭、手機星期日開頭）。共用同一個常數才能保證兩邊永遠一致。
import MobileMonthCalendar, { WEEKDAYS } from '../mobile/components/MobileMonthCalendar';
import LoadingOverlay from '../components/LoadingOverlay';
import { formatDateTime } from '../utils/dateUtils';
import { getGradeLabelOverride } from '../utils/gradeLabels';

const getTaipeiToday = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(new Date());

// datetime-local 的 min / 比較用：台北現在時間，格式 YYYY-MM-DDTHH:mm（字串可直接比大小）
const getTaipeiNowLocalInput = () => new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
}).format(new Date()).replace(' ', 'T');

const buildCalendarCells = (year, month) => {
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    // 星期日開頭（日→六）。JS 的 getDay() 本來就是 0=日…6=六，直接拿來當前置空格數即可。
    // 舊版寫 (getDay()+6)%7 是星期一開頭，跟訂餐/請假/補課三頁以及手機月曆元件都不一致。
    //
    // ⚠️ 這一行決定「日期格子的位移」，跟下面的星期表頭文字是同一件事的兩半，
    // 只改其中一邊會讓每一天都對到錯的欄位。改完的直接好處：格子的欄索引 (i % 7)
    // 從此等於該日的 getDay()，週末判斷不必再另外 new Date 算一次。
    const startPad = firstDay.getDay();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
};

const normalizeBlock = (block) => ({
    label: block.label || '全班',
    type: block.type || 'text',
    text: block.text ?? '',
    photo_urls: block.photo_urls || [],
});

// ---- 未儲存內容的本機暫存 ----------------------------------------------------
// 老師輸入到一半、還沒按儲存時，頁面可能被重設或重載（token 換新、新版部署自動重載、誤關頁面、
// 當機）。內容自動暫存在這台裝置的 localStorage，下次載入同一份聯絡簿時還原，儲存成功後清除。
// key 含使用者 id，同一台共用電腦上不同老師的草稿不會互相看到；超過 7 天的草稿會被丟棄。
const DRAFT_PREFIX = 'commbook-draft:v1:';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// 比較用的序列化。照片只看已上傳的網址：blob: 本機預覽在重新整理後就失效，無法還原，
// 也不該讓「只新增了一張還沒上傳的照片」被當成有文字草稿要保存。
const serializeBlocks = (blocks) => JSON.stringify((blocks || []).map(b => ({
    label: b.label,
    type: b.type,
    text: b.text ?? '',
    photo_urls: (b.photo_urls || []).filter(u => !u.startsWith('blob:')),
})));

const draftKeyFor = (userId, bookKey) => `${DRAFT_PREFIX}${userId ?? 'anon'}:${bookKey}`;

const readDraft = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const d = JSON.parse(raw);
        const age = Date.now() - new Date(d?.savedAt).getTime();
        if (!d || !Array.isArray(d.blocks) || d.blocks.length === 0 || !(age < DRAFT_MAX_AGE_MS)) {
            localStorage.removeItem(key);
            return null;
        }
        return d;
    } catch {
        return null;
    }
};

const writeDraft = (key, blocks, base) => {
    try {
        localStorage.setItem(key, JSON.stringify({
            blocks: JSON.parse(serializeBlocks(blocks)),
            base,
            savedAt: new Date().toISOString(),
        }));
    } catch {
        // 無痕模式或容量已滿：暫存只是保險，失敗就略過，不影響編輯
    }
};

const clearDraft = (key) => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
};

const purgeStaleDrafts = () => {
    try {
        const stale = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k || !k.startsWith(DRAFT_PREFIX)) continue;
            try {
                const d = JSON.parse(localStorage.getItem(k));
                if (!(Date.now() - new Date(d?.savedAt).getTime() < DRAFT_MAX_AGE_MS)) stale.push(k);
            } catch {
                stale.push(k);
            }
        }
        stale.forEach(k => localStorage.removeItem(k));
    } catch {
        /* ignore */
    }
};

const GRADE_OPTIONS = [
    { value: '全班', label: '全班事項' },
    { value: '一年級', label: '一年級' },
    { value: '二年級', label: '二年級' },
    { value: '三年級', label: '三年級' },
    { value: '四年級', label: '四年級' },
    { value: '五年級', label: '五年級' },
    { value: '六年級', label: '六年級' },
    { value: '七年級', label: '七年級 (國一)' },
    { value: '八年級', label: '八年級 (國二)' },
    { value: '九年級', label: '九年級 (國三)' },
    { value: '英文', label: '英文作業' },
];

const STATUS_COLORS = {
    sent: { bg: 'green.50', border: 'green.300', dot: 'green.400', label: '已推播', colorScheme: 'green' },
    scheduled: { bg: 'orange.50', border: 'orange.300', dot: 'orange.400', label: '已預約', colorScheme: 'orange' },
    draft: { bg: 'yellow.50', border: 'yellow.300', dot: 'yellow.400', label: '草稿', colorScheme: 'yellow' },
};

const BookImage = ({ src, onDelete, canDelete, onPreview }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const imageUrl = resolveImageUrl(src);
    // 載入失敗的圖沒東西可放大
    const canPreview = !!onPreview && !isError;

    return (
        <Box
            position="relative" w="100%" pt="100%" borderRadius="md" overflow="hidden" bg="gray.100"
            cursor={canPreview ? 'zoom-in' : undefined}
            onClick={canPreview ? onPreview : undefined}
        >
            {!isLoaded && !isError && <Skeleton position="absolute" top={0} left={0} w="100%" h="100%" />}
            {isError && (
                <VStack position="absolute" top={0} left={0} w="100%" h="100%" justify="center" bg="gray.200" color="gray.500">
                    <Icon as={FiImage} boxSize={8} />
                    <Text fontSize="xs">無法顯示</Text>
                </VStack>
            )}
            <Image
                src={imageUrl}
                onLoad={() => setIsLoaded(true)}
                onError={() => { setIsLoaded(true); setIsError(true); }}
                position="absolute" top={0} left={0} w="100%" h="100%"
                objectFit="cover"
                opacity={isLoaded && !isError ? 1 : 0}
                transition="opacity 0.3s ease-in-out"
            />
            {canDelete && (
                <IconButton
                    icon={<FiTrash2 />} size="xs" colorScheme="red"
                    position="absolute" top={1} right={1}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }} aria-label="Remove photo" zIndex={2}
                />
            )}
        </Box>
    );
};

const CommunicationBookPage = () => {
    const { user } = useAuth();
    const toast = useToast();
    const location = useLocation();
    const navState = location.state || {}; // 手機首頁班級卡「發聯絡簿」帶入：{ classId, openEditor }
    const isMobile = useBreakpointValue({ base: true, md: false });
    // [A2] 年級顯示依登入者機構 grade_labels 渲染；沒設定（天籟現況）時 fallback 回現行文字
    const gradeLabels = user?.institution?.grade_labels || null;

    const todayStr = getTaipeiToday();
    const todayParts = todayStr.split('-').map(Number);

    const [loading, setLoading] = useState(true);
    const [classes, setClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState(null);

    // Calendar navigation
    const [viewMode, setViewMode] = useState('calendar');
    const [viewYear, setViewYear] = useState(todayParts[0]);
    const [viewMonth, setViewMonth] = useState(todayParts[1]);
    const [monthlySummary, setMonthlySummary] = useState([]);
    const [loadingCalendar, setLoadingCalendar] = useState(false);

    // Editor state
    const [selectedDate, setSelectedDate] = useState(todayStr);
    const [bookId, setBookId] = useState(null);
    const [bookScheduledAt, setBookScheduledAt] = useState(null);
    const [bookPushedAt, setBookPushedAt] = useState(null);
    const [contentBlocks, setContentBlocks] = useState([
        { label: '全班', type: 'text', text: '', photo_urls: [] }
    ]);
    const [students, setStudents] = useState([]);
    const [feedbacks, setFeedbacks] = useState({});
    const [signatures, setSignatures] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatusText, setSaveStatusText] = useState('');
    // 送出前選好但還沒真正上傳的照片：key 是本機 blob 網址、value 是原始 File
    const pendingFilesRef = useRef(new Map());

    // Schedule modal
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const [scheduleDateTime, setScheduleDateTime] = useState('');
    const [isSending, setIsSending] = useState(false);

    const photoBlockInputRefs = useRef({});

    // 照片放大檢視（存的是原始 url，顯示時再 resolveImageUrl）
    const [lightbox, setLightbox] = useState(null);
    // 已推播的聯絡簿改內容＝「更新」：家長端與對話卡片是即時讀書本內容，改了就馬上生效，所以要先確認
    const [isUpdateConfirmOpen, setIsUpdateConfirmOpen] = useState(false);
    const updateCancelRef = useRef(null);

    const isPublished = !!bookPushedAt;
    const isFutureDate = selectedDate > todayStr;

    // 預設班級／日期／畫面只在第一次載入時套用一次（見 fetchInitData）
    const initDoneRef = useRef(false);

    // 未儲存內容的暫存：
    //   loadedKeyRef   目前畫面上內容所屬的「班級:日期」（載入完成才有值，載入中為 null）
    //   baselineRef    目前版本（剛載入或剛儲存）的序列化，用來判斷有沒有尚未儲存的修改
    //   serverBlocksRef 後端版本，「捨棄草稿」時用
    //   latestRef      給 pagehide／卸載時的 flush 讀最新內容（那些 handler 不能依賴 render 當下的 state）
    const loadedKeyRef = useRef(null);
    const baselineRef = useRef('');
    const serverBlocksRef = useRef(null);
    const latestRef = useRef({ contentBlocks: [], userId: null });
    const [draftNotice, setDraftNotice] = useState(null); // { savedAt, serverChanged } | null

    const hasUser = !!user;
    // 老師的班級清單靠 user.teaching_classes。AuthContext 每次換發 token 都會先把 user 換成只有
    // id／role 的精簡版、等 getMe() 回來才補齊，所以要等 teaching_classes 出現才算完整。
    // 其他角色（管理員等）看得到全部班級，有 role 就夠了。
    const userReady = hasUser && (user.role !== 'teacher' || user.teaching_classes !== undefined);

    // 【為什麼不再用 [user] 當依賴】token 15 分鐘換新一次，每次 user 物件都會被換掉兩次；
    // 原本這裡每次都重新初始化，把老師正在編輯的班級／日期重設成預設值並重抓資料，
    // 輸入到一半的內容因此被洗掉。現在只在「資料完整了」這個狀態翻轉時才跑。
    useEffect(() => {
        if (!hasUser) return undefined;
        if (userReady) {
            fetchInitData();
            return undefined;
        }
        // getMe() 失敗或很慢時 user 會一直是精簡版：最多等 3 秒，就用現有資料初始化，避免整頁空白
        if (initDoneRef.current) return undefined;
        const t = setTimeout(fetchInitData, 3000);
        return () => clearTimeout(t);
    }, [hasUser, userReady]);

    useEffect(() => {
        if (selectedClassId) fetchMonthlySummary();
    }, [selectedClassId, viewYear, viewMonth]);

    useEffect(() => {
        if (selectedClassId && viewMode === 'editor') fetchBookData();
    }, [selectedClassId, selectedDate, viewMode]);

    // 把目前畫面上的內容跟「已載入／已儲存的版本」比對：有差異就寫進本機暫存，沒差異就清掉。
    // 一律用 loadedKeyRef 當 key（而不是目前選的班級／日期），確保內容和它所屬的聯絡簿不會錯配。
    const flushDraft = useCallback(() => {
        const loadedKey = loadedKeyRef.current;
        if (!loadedKey) return;
        const { contentBlocks: blocks, userId } = latestRef.current;
        const key = draftKeyFor(userId, loadedKey);
        if (serializeBlocks(blocks) === baselineRef.current) clearDraft(key);
        else writeDraft(key, blocks, baselineRef.current);
    }, []);

    // 宣告順序有意義：必須排在下面的 debounce effect 之前，flush 才讀得到最新內容
    useEffect(() => {
        latestRef.current = { contentBlocks, userId: user?.id };
    });

    // 輸入停下來 0.5 秒後暫存。loading 中不存：那時畫面上還是上一份的內容
    useEffect(() => {
        if (loading) return undefined;
        const t = setTimeout(flushDraft, 500);
        return () => clearTimeout(t);
    }, [contentBlocks, loading, flushDraft]);

    // 頁面被關閉／切到背景／元件卸載時立刻存一次，不等 debounce
    useEffect(() => {
        purgeStaleDrafts();
        const onHidden = () => { if (document.visibilityState === 'hidden') flushDraft(); };
        window.addEventListener('pagehide', flushDraft);
        document.addEventListener('visibilitychange', onHidden);
        return () => {
            window.removeEventListener('pagehide', flushDraft);
            document.removeEventListener('visibilitychange', onHidden);
            flushDraft();
        };
    }, [flushDraft]);

    const handleDiscardDraft = () => {
        if (loadedKeyRef.current) clearDraft(draftKeyFor(user?.id, loadedKeyRef.current));
        setContentBlocks(serverBlocksRef.current || [{ label: '全班', type: 'text', text: '', photo_urls: [] }]);
        setDraftNotice(null);
    };

    const fetchInitData = async () => {
        // 後續重跑（token 換新後 user 補齊）只更新班級清單，不能再動老師目前的選擇
        const alreadyInitialized = initDoneRef.current;
        try {
            const res = await authAPI.getClassesLite();
            let teacherClasses = filterClassesForStaffUser(res.data, user);
            if (teacherClasses.length > 0) {
                setClasses(teacherClasses);
                if (!initDoneRef.current) {
                    initDoneRef.current = true;
                    const preferred = navState.classId && teacherClasses.some(c => c.id === navState.classId)
                        ? navState.classId
                        : teacherClasses[0].id;
                    setSelectedClassId(preferred);
                    if (navState.openEditor) {
                        setSelectedDate(todayStr);
                        setViewMode('editor');
                    }
                }
            } else if (!initDoneRef.current) {
                setClasses([]);
                setSelectedClassId(null);
            }
        } catch (error) {
            console.error(error);
            toast({ title: "無法載入班級", status: "error" });
        } finally {
            // 重跑時不能碰 loading：此刻可能正在載入老師剛切換的那份聯絡簿
            if (!alreadyInitialized) setLoading(false);
        }
    };

    const fetchMonthlySummary = async () => {
        setLoadingCalendar(true);
        try {
            const res = await authAPI.communicationBook.getMonthlySummary(selectedClassId, viewYear, viewMonth);
            setMonthlySummary(res.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingCalendar(false);
        }
    };

    const fetchBookData = async () => {
        // 離開目前這份之前，先把還沒寫進暫存的最後幾個字存下來（debounce 可能還沒觸發）
        flushDraft();
        loadedKeyRef.current = null;
        const bookKey = `${selectedClassId}:${selectedDate}`;
        setLoading(true);
        try {
            const res = await authAPI.communicationBook.getByClass(selectedClassId, selectedDate);
            const data = res.data;

            // 套用後端版本；若這份聯絡簿有尚未儲存的本機草稿且內容不同，改顯示草稿並提示。
            // bookKey 取自呼叫當下，所以即使回應晚到，內容與它所屬的 key 也不會錯配。
            const applyLoadedBlocks = (serverBlocks) => {
                const serverSer = serializeBlocks(serverBlocks);
                serverBlocksRef.current = serverBlocks;
                baselineRef.current = serverSer;
                loadedKeyRef.current = bookKey;
                const draft = readDraft(draftKeyFor(user?.id, bookKey));
                if (draft && serializeBlocks(draft.blocks) !== serverSer) {
                    setContentBlocks(draft.blocks.map(normalizeBlock));
                    setDraftNotice({ savedAt: draft.savedAt, serverChanged: draft.base !== serverSer });
                } else {
                    setContentBlocks(serverBlocks);
                    setDraftNotice(null);
                }
            };

            if (data.book) {
                setBookId(data.book.id);
                setBookScheduledAt(data.book.scheduled_at || null);
                setBookPushedAt(data.book.pushed_at || null);

                let loadedBlocks = (data.book.content && data.book.content.length > 0)
                    ? data.book.content.map(normalizeBlock)
                    : [{ label: '全班', type: 'text', text: '', photo_urls: [] }];

                const hasPhotoBlocks = loadedBlocks.some(b => b.type === 'photo');
                const oldPhotos = data.book.photos || [];
                if (!hasPhotoBlocks && oldPhotos.length > 0) {
                    loadedBlocks = [...loadedBlocks, { label: '全班', type: 'photo', text: '', photo_urls: oldPhotos }];
                }

                applyLoadedBlocks(loadedBlocks);

                const fbMap = {};
                (data.book.feedbacks || []).forEach(f => { fbMap[f.student_id] = f; });
                setFeedbacks(fbMap);

                const sigMap = {};
                (data.book.signatures || []).forEach(s => { sigMap[s.student_id] = s; });
                setSignatures(sigMap);
            } else {
                setBookId(null);
                setBookScheduledAt(null);
                setBookPushedAt(null);
                applyLoadedBlocks([{ label: '全班', type: 'text', text: '', photo_urls: [] }]);
                setFeedbacks({});
                setSignatures({});
            }
            setStudents(data.students || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // --- Block handlers ---
    const handleBlockChange = (index, field, value) => {
        const newBlocks = [...contentBlocks];
        newBlocks[index] = { ...newBlocks[index], [field]: value };
        setContentBlocks(newBlocks);
    };

    const addBlock = (type) => {
        const newBlock = type === 'photo'
            ? { label: '全班', type: 'photo', text: '', photo_urls: [] }
            : { label: '全班', type: 'text', text: '', photo_urls: [] };
        setContentBlocks(prev => [...prev, newBlock]);
    };

    const removeBlock = (index) => {
        if (contentBlocks.length === 1) return;
        setContentBlocks(prev => prev.filter((_, i) => i !== index));
    };

    // 選好照片先只存本機預覽，真正上傳延後到「儲存草稿」時才做（避免移除/放棄草稿產生孤兒圖片）
    const handleBlockPhotoUpload = (e, blockIndex) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        const newUrls = Array.from(files).map(file => {
            const localUrl = URL.createObjectURL(file);
            pendingFilesRef.current.set(localUrl, file);
            return localUrl;
        });
        setContentBlocks(prev => {
            const updated = [...prev];
            updated[blockIndex] = {
                ...updated[blockIndex],
                photo_urls: [...(updated[blockIndex].photo_urls || []), ...newUrls]
            };
            return updated;
        });
        e.target.value = '';
    };

    const removePhotoFromBlock = (blockIndex, photoIndex) => {
        setContentBlocks(prev => {
            const updated = [...prev];
            const removedUrl = updated[blockIndex].photo_urls[photoIndex];
            if (removedUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(removedUrl);
                pendingFilesRef.current.delete(removedUrl);
            }
            updated[blockIndex] = {
                ...updated[blockIndex],
                photo_urls: updated[blockIndex].photo_urls.filter((_, i) => i !== photoIndex)
            };
            return updated;
        });
    };

    // --- Calendar navigation ---
    const prevMonth = () => {
        if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12); }
        else setViewMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1); }
        else setViewMonth(m => m + 1);
    };

    const handleCellClick = (dateStr) => {
        if (!dateStr) return;
        setSelectedDate(dateStr);
        setViewMode('editor');
    };

    const handleBackToCalendar = () => {
        setViewMode('calendar');
        fetchMonthlySummary();
    };

    // --- Save ---
    const handleSaveBook = async () => {
        if (!selectedClassId) return;
        setIsSaving(true);
        try {
            // 找出所有區塊裡還沒真正上傳的本機照片，儲存前依序上傳、換成真網址
            const pendingEntries = [];
            contentBlocks.forEach((block, bIdx) => {
                (block.photo_urls || []).forEach((url, pIdx) => {
                    if (url.startsWith('blob:')) pendingEntries.push({ bIdx, pIdx, url });
                });
            });
            const total = pendingEntries.length;
            const urlMap = new Map();
            for (let i = 0; i < pendingEntries.length; i++) {
                const { url } = pendingEntries[i];
                setSaveStatusText(`上傳照片中 (${i + 1}/${total})`);
                const file = pendingFilesRef.current.get(url);
                const fd = new FormData();
                fd.append('files', file, file.name || 'photo.jpg');
                const res = await authAPI.communicationBook.uploadPhoto(fd);
                const real = res.data?.urls?.[0] || res.data?.url;
                if (!real) throw new Error('上傳回應格式異常');
                urlMap.set(url, real);
                URL.revokeObjectURL(url);
                pendingFilesRef.current.delete(url);
            }

            setSaveStatusText(total > 0 ? '儲存中...' : '');
            const resolvedBlocks = contentBlocks.map(block => ({
                ...block,
                photo_urls: (block.photo_urls || []).map(url => urlMap.get(url) || url)
            }));

            const commonData = { content: resolvedBlocks, photos: [], scheduled_at: null };
            let res;
            if (bookId) {
                res = await authAPI.communicationBook.updateBook(bookId, commonData);
            } else {
                res = await authAPI.communicationBook.createOrUpdate({
                    class_id: selectedClassId, date: selectedDate, ...commonData
                });
            }
            if (res.data && res.data.id) setBookId(res.data.id);
            // 這一版已經是後端版本：之後的比對以它為準，並清掉暫存的草稿與提示
            baselineRef.current = serializeBlocks(resolvedBlocks);
            serverBlocksRef.current = resolvedBlocks;
            if (loadedKeyRef.current) clearDraft(draftKeyFor(user?.id, loadedKeyRef.current));
            setDraftNotice(null);
            setContentBlocks(resolvedBlocks);
            toast({ title: isPublished ? "聯絡簿已更新" : "聯絡簿已儲存 (尚未發送)", status: "success" });
        } catch (error) {
            toast({ title: isPublished ? "更新失敗" : "儲存失敗", status: "error", description: error?.message || '' });
        } finally {
            setIsSaving(false);
            setSaveStatusText('');
        }
    };

    // --- Send actions ---
    const handleSendNow = async () => {
        if (!bookId) { toast({ title: "請先儲存聯絡簿", status: "warning" }); return; }
        try {
            setIsSending(true);
            await authAPI.communicationBook.sendAll(selectedClassId, selectedDate, null);
            toast({ title: "已發送推播通知給家長", status: "success" });
            fetchBookData();
            fetchMonthlySummary();
        } catch (error) {
            if (error.response?.status === 409) {
                toast({ title: "此聯絡簿已推播，不可重複發送", status: "warning" });
            } else {
                toast({ title: "發送失敗", status: "error", description: error.response?.data?.detail });
            }
        } finally {
            setIsSending(false);
        }
    };

    const handleScheduleSend = async () => {
        if (!bookId) { toast({ title: "請先儲存聯絡簿後再排程", status: "warning" }); return; }
        if (!scheduleDateTime) { toast({ title: "請選擇發送時間", status: "warning" }); return; }
        // 早於現在的排程時間，後端會當成「立即發送」處理，所以這裡直接擋下來
        if (scheduleDateTime <= getTaipeiNowLocalInput()) {
            toast({ title: "排程時間必須晚於現在", description: "要馬上發送請用「立即發送」", status: "warning" });
            return;
        }
        try {
            setIsSending(true);
            await authAPI.communicationBook.sendAll(selectedClassId, selectedDate, scheduleDateTime);
            toast({ title: "已排程發送", status: "success" });
            setIsScheduleOpen(false);
            fetchBookData();
            fetchMonthlySummary();
        } catch (error) {
            toast({ title: "排程失敗", status: "error", description: error.response?.data?.detail });
        } finally {
            setIsSending(false);
        }
    };

    const handleCancelSchedule = async () => {
        if (!bookId) return;
        try {
            await authAPI.communicationBook.cancelSchedule(bookId);
            toast({ title: "已取消排程", status: "success" });
            fetchBookData();
            fetchMonthlySummary();
        } catch (error) {
            toast({ title: "取消排程失敗", status: "error" });
        }
    };

    const handleFeedbackSave = useCallback(async (studentId, feedbackData) => {
        if (!bookId) { toast({ title: "請先儲存左側聯絡簿內容", status: "warning" }); return; }
        try {
            await authAPI.communicationBook.updateFeedback(bookId, {
                student_id: studentId, comment: feedbackData.comment,
                scores: feedbackData.scores, mood: feedbackData.mood, health: feedbackData.health
            });
            setFeedbacks(prev => ({ ...prev, [studentId]: { ...prev[studentId], ...feedbackData } }));
        } catch (error) {
            toast({ title: "回饋儲存失敗", status: "error" });
        }
    }, [bookId]);

    // --- Calendar cell status ---
    const summaryMap = {};
    monthlySummary.forEach(item => { summaryMap[item.date] = item; });

    const getCellStatus = (dateStr) => {
        const item = summaryMap[dateStr];
        if (!item || !item.has_content) return null;
        if (item.is_sent) return 'sent';
        if (item.scheduled_at) return 'scheduled';
        return 'draft';
    };

    const calendarCells = buildCalendarCells(viewYear, viewMonth);
    const numRows = calendarCells.length / 7;

    // Current book status for editor
    const currentBookStatus = bookPushedAt ? 'sent'
        : bookScheduledAt ? 'scheduled'
        : bookId ? 'draft'
        : null;

    // ===================== CALENDAR VIEW =====================
    if (viewMode === 'calendar') {
        // 手機版：緊湊月曆(跟請假/訂餐/補課共用元件)，班級選單獨立一排，不佔滿版面。
        if (isMobile) {
            return (
                // 不自己鋪底色，讓外殼畫布透出來（桌面 AdminLayout 米白 / 老師 PWA 霧灰橄欖）。
                // 原本寫死 #F7FAFC 是冷調近白，換配色後跟兩個外殼都不搭；訂餐管理也是不設 bg。
                <Box h="100%" display="flex" flexDirection="column" overflow="auto" p={3}>
                    <Select
                        value={selectedClassId || ''}
                        onChange={(e) => setSelectedClassId(parseInt(e.target.value))}
                        bg="white" size="sm" mb={3} flexShrink={0}
                    >
                        {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Select>

                    <Box flexShrink={0}>
                        <MobileMonthCalendar
                            year={viewYear} month={viewMonth} today={todayStr} loading={loadingCalendar}
                            onPrevMonth={prevMonth} onNextMonth={nextMonth}
                            onDayClick={handleCellClick}
                            accentColor="brand" rowHeight="63px"
                            getCellStyle={(dateStr) => {
                                const status = getCellStatus(dateStr);
                                if (!status) return undefined;
                                return { bg: STATUS_COLORS[status].bg };
                            }}
                            renderDay={(dateStr) => {
                                const status = getCellStatus(dateStr);
                                const item = summaryMap[dateStr];
                                if (!status || !item) return null;
                                return (
                                    <Box pos="absolute" bottom="4px" left="0" right="0" textAlign="center">
                                        {item.is_sent ? (
                                            <Text fontSize="xs" fontWeight="bold" color={`${STATUS_COLORS[status].colorScheme}.600`}>
                                                {item.signature_count}/{item.student_count}
                                            </Text>
                                        ) : (
                                            <Box w="6px" h="6px" borderRadius="full" bg={STATUS_COLORS[status].dot} mx="auto" />
                                        )}
                                    </Box>
                                );
                            }}
                        />
                    </Box>

                    <HStack spacing={3} flexWrap="wrap" mt={3} px={1} flexShrink={0}>
                        {Object.entries(STATUS_COLORS).map(([key, val]) => (
                            <HStack key={key} spacing={1}>
                                <Box w="8px" h="8px" borderRadius="full" bg={val.dot} />
                                {/* 圖例直接壓在畫布上（不在卡片裡），所以走 --fg-* 讓兩個外殼各自給對的前景色 */}
                                <Text fontSize="10px" color="var(--fg-body)">{val.label}</Text>
                            </HStack>
                        ))}
                    </HStack>
                </Box>
            );
        }

        return (
            // 同上：底色交給外殼，不要再寫死冷調近白（#F7FAFC 在米白畫布上會泛藍、像沒套主題）
            <Box h="100%" display="flex" flexDirection="column" overflow="hidden">
                {/* Header */}
                <HStack justify="space-between" px={6} py={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
                    <HStack spacing={4}>
                        <Heading size="md" display={{ base: 'none', md: 'block' }}>電子聯絡簿</Heading>
                        <Select
                            value={selectedClassId || ''}
                            onChange={(e) => setSelectedClassId(parseInt(e.target.value))}
                            w="160px" size="sm" bg="gray.50"
                        >
                            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Select>
                    </HStack>
                    <HStack bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" px={3} py={1} spacing={2}>
                        <IconButton icon={<FiChevronLeft />} size="sm" variant="ghost" onClick={prevMonth} aria-label="上個月" />
                        <Text fontWeight="bold" minW="110px" textAlign="center">{viewYear}年 {viewMonth}月</Text>
                        <IconButton icon={<FiChevronRight />} size="sm" variant="ghost" onClick={nextMonth} aria-label="下個月" />
                    </HStack>
                </HStack>

                {/* Legend */}
                <HStack px={6} py={2} spacing={4} bg="white" borderBottom="1px solid" borderColor="gray.100">
                    {Object.entries(STATUS_COLORS).map(([key, val]) => (
                        <HStack key={key} spacing={1}>
                            <Box w="10px" h="10px" borderRadius="full" bg={val.dot} />
                            <Text fontSize="xs" color="gray.600">{val.label}</Text>
                        </HStack>
                    ))}
                    <Text fontSize="xs" color="gray.400" ml={2}>點擊日期編輯</Text>
                </HStack>

                {/* 月曆：整塊外框 + 內部格線。
                    改法直接沿用訂餐管理 / 請假管理 / 補課管理三頁的月曆結構（外框 1.5px、
                    格線 1px、星期表頭包在框內），不另外發明一套。
                    原本是 gap={2} 撐開的圓角浮動格子——在換配色後每一格都是米白、格與格之間
                    露出的也是米白，等於沒有分界，所以要改成靠線條切割。 */}
                <Box
                    flex="1"
                    mx={6} mt={3} mb={4}
                    display="flex"
                    flexDirection="column"
                    border="1.5px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    boxShadow="sm"
                    bg="white"
                    overflow="hidden"
                >
                    {/* 星期表頭：日 → 六（與 buildCalendarCells 的 startPad 是一組的，不可只改一邊） */}
                    <Grid templateColumns="repeat(7, 1fr)" gap={0} bg="gray.50" borderBottom="1.5px solid" borderColor="gray.200">
                        {WEEKDAYS.map((w, idx) => (
                            <GridItem key={w} borderLeft={idx === 0 ? 'none' : '1px solid'} borderColor="gray.100">
                                <Text
                                    textAlign="center" fontSize="xs" fontWeight="bold" py={2}
                                    color={idx === 0 || idx === 6 ? 'brand.400' : 'gray.500'}
                                >
                                    {w}
                                </Text>
                            </GridItem>
                        ))}
                    </Grid>

                    {/* 日期格子 */}
                    <Grid flex="1" templateColumns="repeat(7, 1fr)" templateRows={`repeat(${numRows}, 1fr)`} gap={0}>
                        {calendarCells.map((dateStr, i) => {
                            // 星期日開頭之後，欄索引就等於該日的 getDay()，週末不必再 new Date 算一次
                            const col = i % 7;
                            const isWeekend = col === 0 || col === 6;
                            const isLastRow = Math.floor(i / 7) === numRows - 1;
                            // 最右欄不畫右線、最後一列不畫下線，否則會跟外框疊成兩條
                            const cellBorder = {
                                borderRight: col < 6 ? '1px solid' : 'none',
                                borderBottom: !isLastRow ? '1px solid' : 'none',
                                borderColor: 'gray.100',
                            };

                            if (!dateStr) {
                                return <GridItem key={`empty-${i}`} {...cellBorder} bg="gray.50" />;
                            }

                            const status = getCellStatus(dateStr);
                            const colors = status ? STATUS_COLORS[status] : null;
                            const isToday = dateStr === todayStr;
                            const dayNum = parseInt(dateStr.split('-')[2], 10);
                            const item = summaryMap[dateStr];

                            // 底色優先序：有內容 > 今日 > 週末 > 一般（與訂餐管理同一套規則）
                            const cellBg = colors ? colors.bg : isToday ? 'brand.50' : isWeekend ? 'gray.50' : 'white';
                            const hoverBg = colors ? `${colors.colorScheme}.100` : isToday ? 'brand.100' : 'gray.50';

                            return (
                                <GridItem
                                    key={dateStr}
                                    {...cellBorder}
                                    // 今日改用頂端色條標記：格線版沒有 gap，再給整格描邊會跟隔壁的格線疊在一起
                                    borderTop={isToday ? '3px solid' : '0px'}
                                    borderTopColor={isToday ? 'brand.400' : 'transparent'}
                                >
                                    <Box
                                        h="100%" p={2}
                                        bg={cellBg}
                                        cursor="pointer"
                                        _hover={{ bg: hoverBg }}
                                        onClick={() => handleCellClick(dateStr)}
                                        display="flex"
                                        flexDirection="column"
                                        position="relative"
                                        transition="background 0.15s"
                                    >
                                        <HStack justify="space-between" align="flex-start">
                                            <Text
                                                fontSize="sm" fontWeight={isToday ? 'bold' : 'medium'}
                                                color={isToday ? 'brand.600' : isWeekend ? 'gray.400' : 'gray.700'}
                                            >
                                                {dayNum}
                                            </Text>
                                            {status && (
                                                <Box w="8px" h="8px" borderRadius="full" bg={colors.dot} mt="2px" />
                                            )}
                                        </HStack>
                                        {item && (
                                            <VStack align="start" spacing={0} mt="auto">
                                                {status && (
                                                    <Badge colorScheme={colors.colorScheme} fontSize="0.9rem" variant="subtle">
                                                        {colors.label}
                                                    </Badge>
                                                )}
                                                {item.is_sent && (
                                                    <Text fontSize="0.9rem" color="gray.600" fontWeight="medium">
                                                        簽回 {item.signature_count}/{item.student_count}
                                                    </Text>
                                                )}
                                                {item.is_sent && item.view_count > 0 && (
                                                    <Text fontSize="0.9rem" color="gray.400">{item.view_count} 人已讀</Text>
                                                )}
                                            </VStack>
                                        )}
                                    </Box>
                                </GridItem>
                            );
                        })}
                    </Grid>
                </Box>
            </Box>
        );
    }

    // ===================== EDITOR VIEW =====================
    return (
        <>
            <Grid templateColumns={{ base: "1fr", lg: "350px 1fr 300px" }} h="100%" gap={0}>

                {/* LEFT: Class & Controls */}
                <GridItem bg="white" borderRight="1px solid" borderColor="gray.200" overflowY="auto" p={4}>
                    <VStack align="stretch" spacing={4}>
                        <Button
                            leftIcon={<FiArrowLeft />}
                            variant="ghost"
                            size="sm"
                            colorScheme="brand"
                            justifyContent="flex-start"
                            onClick={handleBackToCalendar}
                            fontWeight="semibold"
                        >
                            電子聯絡簿月曆
                        </Button>

                        <Box>
                            <Text fontWeight="bold" mb={2} color="gray.600">選擇班級</Text>
                            <Select
                                value={selectedClassId || ''}
                                onChange={(e) => setSelectedClassId(parseInt(e.target.value))}
                                bg="gray.50"
                            >
                                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </Select>
                        </Box>

                        <Box>
                            <Text fontWeight="bold" mb={1} color="gray.600">編輯日期</Text>
                            <Text fontSize="lg" fontWeight="semibold" color="gray.800">{selectedDate}</Text>
                            {currentBookStatus && (
                                <Badge
                                    colorScheme={STATUS_COLORS[currentBookStatus].colorScheme}
                                    mt={1}
                                >
                                    {STATUS_COLORS[currentBookStatus].label}
                                </Badge>
                            )}
                        </Box>

                        <Divider />

                        {/* Send actions */}
                        <Box>
                            <Text fontWeight="bold" mb={2} color="gray.600">發送</Text>
                            <VStack align="stretch" spacing={2}>
                                <Button
                                    leftIcon={<FiSend />} colorScheme="green" size="sm"
                                    isDisabled={!bookId || isPublished || isFutureDate}
                                    isLoading={isSending}
                                    onClick={handleSendNow}
                                >
                                    立即發送
                                </Button>
                                {isFutureDate && !isPublished && (
                                    <Text fontSize="xs" color="gray.500">
                                        未來日期無法立即發送，請使用排程發送
                                    </Text>
                                )}
                                {!bookPushedAt && (
                                    <Button
                                        leftIcon={<FiClock />} colorScheme="orange" variant="outline" size="sm"
                                        isDisabled={!bookId}
                                        onClick={() => { setScheduleDateTime(''); setIsScheduleOpen(true); }}
                                    >
                                        {bookScheduledAt ? '修改排程' : '排程發送'}
                                    </Button>
                                )}
                                {bookScheduledAt && !bookPushedAt && (
                                    <Button
                                        leftIcon={<FiX />} colorScheme="red" variant="ghost" size="sm"
                                        onClick={handleCancelSchedule}
                                    >
                                        取消排程
                                    </Button>
                                )}
                                {bookScheduledAt && !bookPushedAt && (
                                    <Text fontSize="xs" color="orange.600">
                                        排程：{formatDateTime(bookScheduledAt)}
                                    </Text>
                                )}
                                {bookPushedAt && (
                                    <Text fontSize="xs" color="green.600">
                                        已推播：{formatDateTime(bookPushedAt)}
                                    </Text>
                                )}
                            </VStack>
                        </Box>

                        <Divider />

                        {/* Student list */}
                        <Box>
                            <HStack justify="space-between" mb={2}>
                                <Text fontWeight="bold" color="gray.600">學生列表 ({students.length})</Text>
                            </HStack>
                            <VStack align="stretch" spacing={2}>
                                {loading && students.length === 0 ? (
                                    [1, 2, 3, 4, 5].map(i => <Skeleton key={i} h="40px" borderRadius="md" />)
                                ) : students.map(s => {
                                    const hasFeedback = !!feedbacks[s.id]?.comment;
                                    const sig = signatures[s.id];
                                    return (
                                        <HStack key={s.id} p={2} bg="gray.50" borderRadius="md" justify="space-between">
                                            <HStack>
                                                <Avatar size="xs" name={s.full_name} src={s.avatar} />
                                                <Text fontSize="sm">{s.full_name}</Text>
                                                {s.grade && <Badge colorScheme="purple" fontSize="xs">{getGradeLabelOverride(s.grade, gradeLabels) || `${s.grade}年級`}</Badge>}
                                            </HStack>
                                            <HStack spacing={1}>
                                                {hasFeedback && <Icon as={FiCheckCircle} color="green.500" />}
                                                {sig?.comment && (
                                                    <Tooltip label={sig.comment} placement="left" hasArrow>
                                                        <span>
                                                            <Icon as={FiMessageSquare} color="orange.400" boxSize={4} />
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {sig && <Badge colorScheme="green" variant="solid" fontSize="0.6rem">已簽</Badge>}
                                            </HStack>
                                        </HStack>
                                    );
                                })}
                            </VStack>
                        </Box>
                    </VStack>
                </GridItem>

                {/* CENTER: Main Editor */}
                {/* 中欄是「卡片底下的那層」，不是卡片本身：用 neutral.50（凹槽色）而不是先前寫死的
                    #F7FAFC 冷調近白。左右兩欄是 white(#FAF6EE) 卡片面，中欄比它再暗 ΔL*2.1，
                    三欄的層級關係才讀得出來，也才跟外殼同一個色溫。 */}
                <GridItem bg="neutral.50" p={6} overflowY="auto">
                    {/* 不覆寫 shadow / borderRadius：交回主題的 Card baseStyle（boxShadow="card"、
                        圓角 2xl），跟其他頁面的卡片才是同一種處理。原本的 shadow="sm" 會把
                        主題特地為米白卡片調的柔和浮起陰影蓋掉，卡片就貼平在底色上了。 */}
                    <Card>
                        <CardBody>
                            <HStack justify="space-between" mb={6}>
                                <HStack>
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="xl" fontWeight="bold">聯絡簿編輯</Text>
                                        <Text color="var(--fg-muted)" fontSize="sm">{selectedDate} • {classes.find(c => c.id === selectedClassId)?.name}</Text>
                                    </VStack>
                                </HStack>
                                {isPublished ? (
                                    <Button leftIcon={<FiRefreshCw />} colorScheme="brand" variant="outline" onClick={() => setIsUpdateConfirmOpen(true)} isLoading={isSaving}>
                                        更新
                                    </Button>
                                ) : (
                                    <Button leftIcon={<FiSave />} colorScheme="brand" variant="outline" onClick={handleSaveBook} isLoading={isSaving}>
                                        儲存草稿
                                    </Button>
                                )}
                            </HStack>

                            <VStack align="stretch" spacing={6}>
                                {loading ? (
                                    <>
                                        <Skeleton h="150px" borderRadius="lg" />
                                        <Skeleton h="150px" borderRadius="lg" />
                                    </>
                                ) : (
                                    <>
                                        {draftNotice && (
                                            <Alert status="warning" borderRadius="md" alignItems="flex-start">
                                                <AlertIcon mt={1} />
                                                <Box flex="1">
                                                    <AlertDescription fontSize="sm">
                                                        已還原你上次尚未儲存的內容（{formatDateTime(draftNotice.savedAt)}）。
                                                        {draftNotice.serverChanged && '這份聯絡簿在那之後已被更新過，儲存會覆蓋較新的內容，請先確認。'}
                                                        確認無誤後請按「{isPublished ? '更新' : '儲存草稿'}」。
                                                    </AlertDescription>
                                                </Box>
                                                <Button size="xs" variant="outline" ml={2} flexShrink={0} onClick={handleDiscardDraft}>
                                                    捨棄草稿
                                                </Button>
                                            </Alert>
                                        )}
                                        {contentBlocks.map((block, idx) => {
                                            const isPhoto = block.type === 'photo';
                                            return (
                                                // 區塊是「卡片內的凹槽」→ gray.50。先前寫 white 等於跟卡片同色，
                                                // 只剩一條邊框在撐，分不出這是巢狀的區塊；凹槽色才讀得出層級
                                                // （區塊內的 Textarea 由主題固定為 white，剛好反過來浮在凹槽上）。
                                                <Box
                                                    key={idx} p={4}
                                                    borderWidth="1px" borderRadius="lg" bg="gray.50"
                                                    borderColor={isPhoto ? "purple.200" : "gray.200"}
                                                >
                                                    <HStack justify="space-between" mb={3}>
                                                        <HStack spacing={2}>
                                                            <Icon as={isPhoto ? FiImage : FiFileText} color={isPhoto ? "purple.500" : "blue.500"} />
                                                            <Select
                                                                w="150px" size="sm"
                                                                value={block.label}
                                                                onChange={(e) => handleBlockChange(idx, 'label', e.target.value)}
                                                            >
                                                                {GRADE_OPTIONS.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </Select>
                                                            <Badge colorScheme={isPhoto ? "purple" : "blue"} fontSize="xs" variant="subtle">
                                                                {isPhoto ? '照片' : '文字'}
                                                            </Badge>
                                                        </HStack>
                                                        {contentBlocks.length > 1 && (
                                                            <IconButton
                                                                icon={<FiTrash2 />} size="xs" variant="ghost"
                                                                colorScheme="red" onClick={() => removeBlock(idx)}
                                                            />
                                                        )}
                                                    </HStack>

                                                    {isPhoto ? (
                                                        <Box>
                                                            <Grid templateColumns="repeat(auto-fill, minmax(90px, 1fr))" gap={3}>
                                                                {(block.photo_urls || []).map((url, pIdx) => (
                                                                    <BookImage
                                                                        key={pIdx} src={url} canDelete={true}
                                                                        onDelete={() => removePhotoFromBlock(idx, pIdx)}
                                                                        onPreview={() => setLightbox(url)}
                                                                    />
                                                                ))}
                                                                <Box
                                                                    position="relative" w="100%" pt="100%"
                                                                    borderWidth="2px" borderStyle="dashed" borderRadius="md"
                                                                    borderColor="purple.300" cursor="pointer"
                                                                    _hover={{ borderColor: 'purple.500', bg: 'purple.50' }}
                                                                    onClick={() => photoBlockInputRefs.current[idx]?.click()}
                                                                >
                                                                    <VStack position="absolute" top={0} left={0} w="100%" h="100%" justify="center" spacing={1}>
                                                                        <Icon as={FiUpload} color="purple.400" />
                                                                        <Text fontSize="xs" color="purple.500">上傳</Text>
                                                                    </VStack>
                                                                </Box>
                                                            </Grid>
                                                            <input
                                                                type="file"
                                                                ref={el => photoBlockInputRefs.current[idx] = el}
                                                                hidden multiple accept="image/*"
                                                                onChange={(e) => handleBlockPhotoUpload(e, idx)}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Textarea
                                                            value={block.text}
                                                            onChange={(e) => handleBlockChange(idx, 'text', e.target.value)}
                                                            placeholder="輸入聯絡事項..."
                                                            rows={8}
                                                        />
                                                    )}
                                                </Box>
                                            );
                                        })}

                                        <HStack spacing={3}>
                                            <Button leftIcon={<FiPlus />} variant="outline" colorScheme="blue" onClick={() => addBlock('text')} flex={1}>
                                                增加文字區塊
                                            </Button>
                                            <Button leftIcon={<FiImage />} variant="outline" colorScheme="purple" onClick={() => addBlock('photo')} flex={1}>
                                                增加照片區塊
                                            </Button>
                                        </HStack>
                                    </>
                                )}
                            </VStack>
                        </CardBody>
                    </Card>
                </GridItem>

                {/* RIGHT: Individual Feedback */}
                <GridItem bg="white" borderLeft="1px solid" borderColor="gray.200" p={4} overflowY="auto">
                    <Text fontWeight="bold" mb={4} fontSize="lg">個別回饋 (選填)</Text>
                    <VStack align="stretch" spacing={6}>
                        {students.map(s => (
                            <StudentFeedbackCard
                                key={s.id} student={s}
                                initialFeedback={feedbacks[s.id]}
                                signature={signatures[s.id]}
                                onSave={handleFeedbackSave}
                                gradeLabels={gradeLabels}
                            />
                        ))}
                    </VStack>
                </GridItem>
            </Grid>

            {/* Schedule Modal */}
            <Modal isOpen={isScheduleOpen} onClose={() => setIsScheduleOpen(false)} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>排程發送</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>發送時間</FormLabel>
                            <Input
                                type="datetime-local"
                                value={scheduleDateTime}
                                min={getTaipeiNowLocalInput()}
                                onChange={e => setScheduleDateTime(e.target.value)}
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => setIsScheduleOpen(false)}>取消</Button>
                        <Button colorScheme="orange" onClick={handleScheduleSend} isLoading={isSending}>
                            確認排程
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* 更新確認：已推播的聯絡簿改內容，家長端與對話卡片會立即看到，先讓老師確認 */}
            <AlertDialog
                isOpen={isUpdateConfirmOpen}
                leastDestructiveRef={updateCancelRef}
                onClose={() => setIsUpdateConfirmOpen(false)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent mx={4}>
                        <AlertDialogHeader fontSize="md" fontWeight="bold">
                            更新這份聯絡簿？
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            <Text fontSize="sm">
                                {selectedDate} 的聯絡簿已經發送給家長。更新後，家長端與對話卡片會立即看到新內容。
                            </Text>
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                不會另外發送推播通知。
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={updateCancelRef} size="sm" onClick={() => setIsUpdateConfirmOpen(false)}>
                                取消
                            </Button>
                            <Button
                                colorScheme="brand" size="sm" ml={3}
                                onClick={() => { setIsUpdateConfirmOpen(false); handleSaveBook(); }}
                            >
                                確認更新
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* 照片放大檢視（react-zoom-pan-pinch，比照老師對話室既有 lightbox 樣式）。
                也涵蓋尚未儲存的本機預覽照片（blob 網址），方便存檔前確認圖片正確。 */}
            <Modal isOpen={!!lightbox} onClose={() => setLightbox(null)} size="full" isCentered>
                <ModalOverlay bg="blackAlpha.900" />
                <ModalContent bg="transparent" boxShadow="none" m={0}>
                    <ModalCloseButton
                        color="white" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.800' }}
                        borderRadius="full" zIndex={200} size="lg"
                        position="fixed" top="calc(env(safe-area-inset-top) + 12px)" right="16px"
                    />
                    <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                        {lightbox && (
                            <TransformWrapper doubleClick={{ mode: 'reset' }}>
                                <TransformComponent
                                    wrapperStyle={{ width: '100vw', height: '100vh' }}
                                    contentStyle={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <Image src={resolveImageUrl(lightbox)} w="100vw" maxH="100vh" objectFit="contain" />
                                </TransformComponent>
                            </TransformWrapper>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <LoadingOverlay isLoading={isSaving} text={saveStatusText || '儲存中...'} />
        </>
    );
};

export default CommunicationBookPage;
